/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { locations } from '@constants';
import { OrderShipping } from '@interfaces';

@Component({
  selector: 'unicon-location-field',
  templateUrl: './location-field.component.html',
  styleUrl: './location-field.component.scss',
})
export class LocationFieldComponent implements OnInit {
  @Input() orderId!: string;
  @Input() shipping!: OrderShipping;
  @Input() isActivated!: boolean;
  @Output() readonly result: EventEmitter<OrderShipping> =
    new EventEmitter<OrderShipping>();
  protected form!: FormGroup;
  protected hovered: boolean = false;
  protected edit: boolean = false;
  protected LOCATIONS: any = locations;
  protected departments: string[] = [];
  protected provinces: string[] = [];
  protected districts: string[] = [];
  protected selectedDepartment: string | null = '';
  protected selectedProvince: string | null = '';
  protected selectedDistrict: string | null = '';

  @ViewChild('departmentBox') departmentBox!: ElementRef<HTMLDivElement>;
  @ViewChild('provinceBox') provinceBox!: ElementRef<HTMLDivElement>;
  @ViewChild('districtBox') districtBox!: ElementRef<HTMLDivElement>;
  @ViewChild('addressNameBox') addressBox!: ElementRef<HTMLDivElement>;
  @ViewChild('latitudeBox') latitudeBox!: ElementRef<HTMLDivElement>;
  @ViewChild('longitudeBox') longitudeBox!: ElementRef<HTMLDivElement>;

  constructor(private formBuilder: FormBuilder) {
    this.setForm();
  }

  ngOnInit(): void {
    this.departments = Object.keys(this.LOCATIONS);
    if (this.shipping) {
      this.form.patchValue(this.shipping);
    }
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      addressName: new FormControl(''),
      department: new FormControl(''),
      province: new FormControl(''),
      district: new FormControl(''),
      latitude: new FormControl(''),
      longitude: new FormControl(''),
    });
  }

  protected shouldCreateShipping(): boolean {
    const shipping = this.shipping as Record<string, string>;
    const formValues: string[] = [
      'addressName',
      'department',
      'province',
      'district',
      'latitude',
      'longitude',
    ];

    if (this.shipping) {
      for (const key of formValues) {
        const value = shipping[key];
        if (value !== null && value !== undefined && value !== '') {
          return false;
        }
      }
    }

    return true;
  }

  protected toggleEdit(): void {
    this.edit = !this.edit;
    if (this.shipping.province !== null && this.shipping.department === null) {
      this.shipping.province = null;
    }
    if (this.shipping) {
      this.form.patchValue(this.shipping);
      if (this.shipping.department) {
        this.selectedDepartment = this.shipping.department;
      }

      if (this.shipping.department && this.shipping.province) {
        this.provinces = this.shipping.department
          ? Object.keys(this.LOCATIONS[this.shipping.department])
          : [];
        this.selectedProvince = this.shipping.province;
        this.form.controls['province'].enable();
      }

      if (
        this.shipping.department &&
        this.shipping.province &&
        this.shipping.district
      ) {
        this.districts = Object.keys(
          this.LOCATIONS[this.shipping.department][this.shipping.province],
        );
        this.selectedDistrict = this.shipping.district;
        this.form.controls['district'].enable();
      }
    }
  }

  protected closeEdit(): void {
    this.edit = false;
    this.enabledFields();
  }

  protected disableCoordinates() {
    this.form.controls['latitude'].disable();
    this.form.controls['longitude'].disable();
  }

  protected disableFields() {
    this.form.controls['department'].disable();
    this.form.controls['province'].disable();
    this.form.controls['district'].disable();
    this.form.controls['addressName'].disable();
  }

  protected enabledFields() {
    this.form.controls['latitude'].enable();
    this.form.controls['longitude'].enable();
    this.form.controls['department'].enable();
    this.form.controls['province'].enable();
    this.form.controls['district'].enable();
    this.form.controls['addressName'].enable();
  }

  protected onFocusDepartment() {
    this.departmentBox.nativeElement.classList.add('border-neutrals-900');
    this.disableCoordinates();
  }

  protected onFocusAddress() {
    this.addressBox.nativeElement.classList.add('border-neutrals-900');
    this.disableCoordinates();
  }

  protected onFocusLongitude() {
    this.longitudeBox.nativeElement.classList.add('border-neutrals-900');
    this.disableFields();
  }

  protected onFocusLatitude() {
    this.latitudeBox.nativeElement.classList.add('border-neutrals-900');
    this.disableFields();
  }

  protected onSelectDepartment(event: Event) {
    const department = event.target as HTMLInputElement;
    const selectedDepartment = department.value;
    if (selectedDepartment) {
      this.selectedDepartment = selectedDepartment;
      this.form.controls['province'].setValue(null);
      this.form.controls['district'].setValue(null);
      this.selectedProvince = null;
      this.selectedDistrict = null;
      this.provinces = selectedDepartment
        ? Object.keys(this.LOCATIONS[selectedDepartment])
        : [];
      this.districts = [];
      this.form.controls['province'].enable();
      this.form.controls['district'].disable();
    }
  }
  protected onSelectProvince(event: Event) {
    const province = event.target as HTMLInputElement;
    const selectedProvince = province.value;
    if (this.selectedDepartment && selectedProvince) {
      this.districts = Object.keys(
        this.LOCATIONS[this.selectedDepartment][selectedProvince],
      );
      this.form.controls['district'].enable();
    } else {
      this.districts = [];
      this.selectedDistrict = null;
    }
  }

  protected onSelectDistrict(event: Event) {
    const district = event.target as HTMLInputElement;
    const selectedDistrict = district.value;
    this.selectedDistrict = selectedDistrict;
  }

  protected onSubmit(): void {
    this.result.emit(this.form.value as OrderShipping);
    this.closeEdit();
  }

  protected openGoogleMaps(data: OrderShipping): void {
    const url = `https://www.google.com/maps?q=${data?.latitude},${data?.longitude}`;
    window.open(url, '_blank');
  }
}
